import { useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';
import { useTTS } from '@/hooks/useTTS';
import type { TTSProps } from '@/types/tts';
import IconButtonSkeleton from '@/components/common/skeleton/IconButtonSkeleton';
import styles from './styles.module.scss';

type IconButtonType = 'button' | 'reset' | 'submit';
type IconButtonFontSize = '14' | '18' | '20' | '22' | '26' | '28' | '32';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, TTSProps {
    icon: ReactNode;
    color?: string;
    width?: number | string;
    height?: number | string;
    disabled?: boolean;
    fullWidth?: boolean;
    loading?: boolean;
    fontSize?: IconButtonFontSize;
    type?: IconButtonType;
    className?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void;
}

const IconButton = ({
    icon,
    color,
    width = 38,
    height = 38,
    disabled = false,
    fullWidth = false,
    loading = false,
    fontSize = '20',
    type = 'button',
    role = 'button',
    ariaLabel,
    ariaDescription,
    ariaChecked,
    ariaPressed,
    ariaSelected,
    ariaValueText,
    ariaValueNow,
    ariaHasPopup,
    ariaExpanded,
    dataRole,
    dataDesc,
    dataInteractive = false,
    value,
    name,
    enableTickSound = true,
    tabIndex,
    ttsTextSelected = 'Selected',
    ttsTextUnselected = 'Unselected',
    className = '',
    onClick,
    ...rest
}: IconButtonProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { cancel } = useTTS();
    const isContrastMode = false;

    const fontSizeClasses = {
        '14': 'text-[14px] font-medium leading-[18px] tracking-[-0.238px]',
        '18': 'text-[18px] font-medium leading-[19px] tracking-[-0.36px]',
        '20': 'text-[20px] font-medium leading-[32px] tracking-[-0.44px]',
        '22': 'text-[22px] font-normal leading-[30px] tracking-[-0.484px]',
        '26': 'text-[26px] font-medium leading-[36px] tracking-[-0.572px]',
        '28': 'text-[28px] font-medium leading-[38px]',
        '32': 'text-[32px] font-medium leading-[42px]',
    };

    const defaultClassName = clsx(
        'font-noto-sans-cjk',
        'relative outline-none box-border',
        'flex items-center overflow-hidden justify-center',
        'hover:cursor-pointer',
        styles['icon-button'],
        fontSizeClasses[fontSize],
        fullWidth && 'w-full',
        disabled && 'opacity-50 cursor-not-allowed',
        isContrastMode && 'bg-primary-yellow text-primary-black',
        color && `text-[${color}]`,
        className
    );

    const handlePress = (event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled || loading) return;

        cancel();

        if (onClick) {
            onClick(event);
        }
    };

    return (
        <button
            ref={buttonRef}
            type={type}
            role={role}
            aria-label={ariaLabel}
            aria-description={ariaDescription}
            aria-checked={ariaChecked}
            aria-pressed={ariaPressed}
            aria-selected={ariaSelected}
            aria-valuetext={ariaValueText}
            aria-valuenow={ariaValueNow}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
            data-role={dataRole}
            data-desc={dataDesc}
            data-interactive={dataInteractive ? 'true' : undefined}
            value={value}
            name={name}
            onClick={handlePress}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePress(e);
                }
            }}
            tabIndex={tabIndex}
            className={defaultClassName}
            style={{ width, height }}
            disabled={disabled}
            {...rest}
        >
            {icon && icon}
            {loading && <IconButtonSkeleton />}
        </button>
    );
};

export default IconButton;
